import type { Player } from '@api-service/players';
import type { Team } from '@api-service/teams';

export const PLAYERS_INDEX_PATH = '/fpl/players';

/**
 * Reserved first segment under /fpl/players. A club slug can never collide
 * with it because club slugs come from team names, but the resolver refuses
 * it explicitly so the routing assumption is written down.
 */
export const BY_ID_SEGMENT = 'by-id';

/**
 * Lowercases, strips diacritics and collapses everything that is not a letter
 * or digit into single hyphens, so names like "Ødegaard" and "O'Shea" produce
 * stable URL-safe slugs (odegaard, o-shea).
 */
export const toSlug = (value: string): string =>
  value
    .normalize('NFKD')
    // Strips the combining diacritical marks left over from NFKD, turning
    // "Gündoğan" into "Gundogan".
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // Letters NFKD cannot decompose, common in Scandinavian and Balkan names.
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .replace(/[đð]/g, 'd')
    .replace(/þ/g, 'th')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const buildClubSlug = (team: Pick<Team, 'name'>): string =>
  toSlug(team.name);

export const resolveTeamBySlug = (
  teams: Team[],
  clubSlug: string
): Team | null =>
  clubSlug === BY_ID_SEGMENT
    ? null
    : teams.find((team) => buildClubSlug(team) === clubSlug) ?? null;

type SluggablePlayer = Pick<Player, 'displayName' | 'externalIds'>;

/**
 * A player's slug is their display name, and only gains a suffix when two
 * squad mates share that name. The suffix is `externalIds.fplCode` because it
 * is the cross-season stable identifier; player UUIDs are re-minted every
 * season and would change the URL each August. When both collide AND the
 * code is missing, the modern Opta UUID's leading characters stand in so the
 * result is still deterministic.
 *
 * Uniqueness only needs to hold within a club, because the club is part of
 * the URL. Collision detection therefore takes the squad, not the league.
 */
export const buildPlayerSlug = (
  player: SluggablePlayer,
  squad: SluggablePlayer[]
): string => {
  const baseSlug = toSlug(player.displayName);
  const hasCollision = squad.some(
    (squadPlayer) =>
      squadPlayer !== player &&
      squadPlayer.externalIds.optaUuid !== player.externalIds.optaUuid &&
      toSlug(squadPlayer.displayName) === baseSlug
  );

  if (!hasCollision) return baseSlug;

  const tiebreaker =
    player.externalIds.fplCode ?? player.externalIds.optaUuid.slice(0, 8);
  return `${baseSlug}-${tiebreaker}`;
};

export const resolvePlayerBySlug = <T extends SluggablePlayer>(
  squad: T[],
  playerSlug: string
): T | null =>
  squad.find((player) => buildPlayerSlug(player, squad) === playerSlug) ?? null;

export type SlugTiebreaker =
  | { kind: 'fplCode'; baseSlug: string; fplCode: number }
  | { kind: 'optaUuidPrefix'; baseSlug: string; uuidPrefix: string };

/** Length of the Opta UUID prefix `buildPlayerSlug` uses as its fallback. */
const UUID_PREFIX_LENGTH = 8;
const UUID_PREFIX_PATTERN = new RegExp(
  `^(.+)-([a-z0-9]{${UUID_PREFIX_LENGTH}})$`
);

/**
 * The ways a slug's tail can be read as a collision tiebreaker, matching the
 * two suffixes `buildPlayerSlug` can emit: the numeric cross-season FPL
 * code, or the leading characters of the Opta UUID when a colliding player
 * has no code. An all-digit tail of the right length is offered as both. A
 * name whose real last token merely looks like a tiebreaker cannot
 * false-positive: every consumer also requires the base name AND the
 * tiebreaker to match the same player.
 */
export const parseSlugTiebreakers = (playerSlug: string): SlugTiebreaker[] => {
  const tiebreakers: SlugTiebreaker[] = [];

  const numericMatch = playerSlug.match(/^(.+)-(\d+)$/);
  if (numericMatch) {
    tiebreakers.push({
      kind: 'fplCode',
      baseSlug: numericMatch[1],
      fplCode: Number(numericMatch[2])
    });
  }

  const uuidMatch = playerSlug.match(UUID_PREFIX_PATTERN);
  if (uuidMatch) {
    tiebreakers.push({
      kind: 'optaUuidPrefix',
      baseSlug: uuidMatch[1],
      uuidPrefix: uuidMatch[2]
    });
  }

  return tiebreakers;
};

export const matchesTiebreaker = (
  player: SluggablePlayer,
  tiebreaker: SlugTiebreaker
): boolean =>
  toSlug(player.displayName) === tiebreaker.baseSlug &&
  (tiebreaker.kind === 'fplCode'
    ? player.externalIds.fplCode === tiebreaker.fplCode
    : player.externalIds.optaUuid.startsWith(tiebreaker.uuidPrefix));

/**
 * Accepts a historical slug variant so published URLs survive collision
 * transitions. Canonical slugs are collision-aware, so when a same-named
 * player leaves the club, the survivor's canonical slug loses its suffix and
 * the old suffixed URL would otherwise 404. The suffix resolves through
 * whichever tiebreaker produced it (name-checked, to reject a stale
 * tiebreaker from a different player's URL). Callers must redirect to the
 * current canonical slug rather than serve the alias: two live URLs for one
 * player would split their search equity.
 *
 * The reverse transition (a bare slug that gained a suffix when a namesake
 * arrived) stays unresolved by design - the bare name is ambiguous between
 * the two players, and guessing would land readers on the wrong profile.
 */
export const resolvePlayerBySlugAlias = <T extends SluggablePlayer>(
  squad: T[],
  playerSlug: string
): T | null => {
  for (const tiebreaker of parseSlugTiebreakers(playerSlug)) {
    const match = squad.find((player) => matchesTiebreaker(player, tiebreaker));
    if (match) return match;
  }
  return null;
};

export const toClubIndexPath = (clubSlug: string): string =>
  `${PLAYERS_INDEX_PATH}/${clubSlug}`;

export const toPlayerProfilePath = (
  clubSlug: string,
  playerSlug: string
): string => `${PLAYERS_INDEX_PATH}/${clubSlug}/${playerSlug}`;

/**
 * Resolver URL for callers that only hold an Opta UUID (search results,
 * in-form cards, clubmate pills). It 301s to the canonical slug URL, so it
 * must never be emitted in server-rendered, indexable markup - crawlable
 * surfaces (index pages, sitemaps) build the slug URL directly instead.
 */
export const toPlayerByIdPath = (optaId: string): string =>
  `${PLAYERS_INDEX_PATH}/${BY_ID_SEGMENT}/${optaId}`;
